<template>
  <div
    aria-label="Création d'une feuille de ramassage"
    role="main"
  >
    <!-- Loading Spinner -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- Repository Selection (Admin/Superadmin only) -->
    <b-form-group
      v-if="user.role === 'admin' || user.role === 'superadmin'"
      label="Dépôt"
      label-for="select-repository"
      aria-label="Sélection du dépôt"
    >
      <v-select
        v-model="repository"
        :clearable="false"
        placeholder="Sélectionner un dépôt"
        label="name"
        :options="repositories"
        input-id="select-repository"
        aria-label="Liste des dépôts disponibles"
        @input="onRepositoryChange"
      />
    </b-form-group>

    <!-- Order Reference Scanner -->
    <b-form-group
      label="Référence du colis à ajouter à la feuille de route"
      label-for="input-colis-ref"
      aria-label="Champ de saisie de la référence du colis"
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon icon="MaximizeIcon" />
        </b-input-group-prepend>
        <b-form-input
          id="input-colis-ref"
          ref="scanInput"
          v-model="ref"
          placeholder="Scanner ou saisir la référence du colis"
          aria-label="Entrer la référence du colis à ajouter"
          @change="checkOrder"
        />
      </b-input-group>
    </b-form-group>

    <!-- Two Tables Side by Side -->
    <div class="d-flex justify-content-between">
      <!-- Available Orders (Colis au dépôt) -->
      <b-card class="flex-grow-1 mr-1">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4
            class="mb-0"
            aria-live="polite"
          >
            Colis en attente : {{ availableOrders.length }}
          </h4>
          <div>
            <b-button
              v-b-tooltip.hover
              :variant="sortBySupplier ? 'primary' : 'outline-primary'"
              size="sm"
              title="Trier par fournisseur"
              @click="toggleSortBySupplier"
            >
              <feather-icon
                icon="UsersIcon"
                class="mr-25"
              />
              {{ sortBySupplier ? 'Trié par fournisseur' : 'Trier par fournisseur' }}
            </b-button>
          </div>
        </div>
        <vue-good-table
          :columns="columns"
          :rows="sortedAvailableOrders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
          :row-style-class="rowStyleClass"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- ID Column -->
            <span v-if="props.column.field === 'id'">
              <span>{{ props.row.id }}</span>
              <br>
              <span
                v-if="props.row.is_an_exchange"
                style="color: red; font-weight: bold;"
              >Echange</span>
            </span>

            <!-- Action Column -->
            <span v-else-if="props.column.field === 'action'">
              <feather-icon
                :id="`add-order-${props.row.id}`"
                icon="CheckSquareIcon"
                class="cursor-pointer mr-1"
                size="16"
                color="green"
                @click="addItem(props.row)"
              />
              <b-tooltip
                title="Ajouter"
                :target="`add-order-${props.row.id}`"
              />
            </span>

            <!-- Default Column -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- Pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">Affichage 1 à</span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50', '100', '500']"
                  class="mx-1"
                  @input="(value) => props.perPageChanged({ currentPerPage: value })"
                />
                <span class="text-nowrap">de {{ props.total }} résultats</span>
              </div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </template>
        </vue-good-table>
      </b-card>

      <!-- Selected Orders (Colis dans la feuille de route) -->
      <b-card class="flex-grow-1 ml-1">
        <h4 aria-live="polite">
          Colis dans la feuille de route : {{ selectedOrders.length }}
        </h4>
        <vue-good-table
          :columns="columns"
          :rows="selectedOrders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- ID Column -->
            <span v-if="props.column.field === 'id'">
              <span>{{ props.row.id }}</span>
              <br>
              <span
                v-if="props.row.is_an_exchange"
                style="color: red; font-weight: bold;"
              >Echange</span>
            </span>

            <!-- Action Column -->
            <span v-else-if="props.column.field === 'action'">
              <feather-icon
                :id="`remove-order-${props.row.id}`"
                icon="XIcon"
                class="cursor-pointer mr-1"
                size="16"
                color="red"
                @click="removeItem(props.row)"
              />
              <b-tooltip
                title="Retirer"
                :target="`remove-order-${props.row.id}`"
              />
            </span>

            <!-- Default Column -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- Pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">Affichage 1 à</span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50', '100', '500']"
                  class="mx-1"
                  @input="(value) => props.perPageChanged({ currentPerPage: value })"
                />
                <span class="text-nowrap">de {{ props.total }} résultats</span>
              </div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </template>
        </vue-good-table>

        <!-- Driver Selection -->
        <b-form-group
          v-if="drivers.length > 0"
          label="Sélectionner le livreur"
          label-for="select-driver"
          class="mt-2"
        >
          <v-select
            v-model="selectedDriver"
            :clearable="false"
            placeholder="Livreur"
            label="username"
            :options="drivers"
          >
            <template #option="{ username, role_display }">
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ username }}</span>
                <b-badge
                  :variant="role_display === 'Ramasseur' ? 'light-info' : 'light-primary'"
                  pill
                  class="ml-1"
                >
                  {{ role_display }}
                </b-badge>
              </div>
            </template>
            <template #selected-option="{ username, role_display }">
              <span>{{ username }}</span>
              <b-badge
                :variant="role_display === 'Ramasseur' ? 'light-info' : 'light-primary'"
                pill
                class="ml-1"
              >
                {{ role_display }}
              </b-badge>
            </template>
          </v-select>
        </b-form-group>

        <!-- Car Selection -->
        <b-form-group
          label="Sélectionner la voiture"
          label-for="select-car"
        >
          <v-select
            v-model="selectedCar"
            :clearable="true"
            placeholder="Voiture"
            label="serial_number"
            :options="cars"
          >
            <template #option="{ brand, serial_number, plate_number }">
              {{ brand }} - {{ serial_number }}
              <span
                v-if="plate_number"
                class="text-muted"
              >({{ plate_number }})</span>
            </template>
            <template #selected-option="{ brand, serial_number }">
              {{ brand }} - {{ serial_number }}
            </template>
          </v-select>
        </b-form-group>

        <!-- Save Button -->
        <b-form-group class="d-flex justify-content-end mt-2">
          <span id="save-pickup-btn-wrapper">
            <b-button
              ref="saveButton"
              variant="gradient-primary"
              class="ml-1 d-inline-block save-btn"
              :class="{ 'not-allowed': isLoading || !isValid }"
              :disabled="isLoading || !isValid"
              @click="createPickupRoadmap"
            >
              <feather-icon icon="PlusIcon" />
              <span class="ml-1">Enregistrer</span>
            </b-button>
            <b-tooltip
              v-if="isLoading || !isValid"
              target="save-pickup-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="isLoading">
                Veuillez patienter, chargement en cours...
              </template>
              <template v-else>
                <span v-if="selectedOrders.length === 0">Veuillez sélectionner au moins un colis.</span>
                <span v-else-if="!selectedDriver">Veuillez sélectionner un livreur.</span>
                <span v-else>Veuillez remplir tous les champs obligatoires.</span>
              </template>
            </b-tooltip>
          </span>
        </b-form-group>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BPagination,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BFormSelect,
  BTooltip,
  BSpinner,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Audio files
import ok from '@/assets/audio/ok.mp3'
import errorVoice from '@/assets/audio/error.mp3'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'

export default {
  name: 'CreatePickupRoadmap',
  components: {
    BCard,
    BButton,
    BPagination,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BTooltip,
    BSpinner,
    BBadge,
    VueGoodTable,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      ref: '',
      searchTerm: '',
      pageLength: 100,

      // Repository
      repository: null,
      repositories: [],

      // Orders
      availableOrders: [],
      selectedOrders: [],
      sortBySupplier: false,

      // Driver & Car
      drivers: [],
      selectedDriver: null,
      cars: [],
      selectedCar: null,

      // Table columns
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher',
          },
        },
        {
          label: 'Fournisseur',
          field: 'supplier_name',
          filterOptions: {
            enabled: true,
            placeholder: 'Fournisseur',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate_name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'Délégation',
          field: 'delegation_name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
        },
        {
          label: 'Adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
      ],

      // Audio
      ok,
      errorVoice,
      alreadyScanned,
    }
  },
  computed: {
    isValid() {
      return this.selectedOrders.length > 0 && this.selectedDriver
    },
    sortedAvailableOrders() {
      if (!this.sortBySupplier) {
        return this.availableOrders
      }
      // Sort by supplier name to group them together
      const sorted = this.availableOrders.slice()
      sorted.sort((a, b) => {
        const supplierA = (a.supplier_name || '').toLowerCase()
        const supplierB = (b.supplier_name || '').toLowerCase()
        return supplierA.localeCompare(supplierB)
      })

      // Add supplier group index for alternating colors
      let currentSupplier = null
      let groupIndex = 0
      const result = sorted.map(order => {
        if (order.supplier_name !== currentSupplier) {
          currentSupplier = order.supplier_name
          groupIndex += 1
        }
        return { ...order, supplierGroupIndex: groupIndex }
      })

      return result
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.loadRepositories()
    this.loadCars()

    // Auto-load orders for manager
    if (this.user.role === 'manager') {
      this.loadAvailableOrders()
      this.loadDrivers()
    }
  },
  mounted() {
    if (this.$refs.scanInput) {
      this.$refs.scanInput.focus()
    }
  },
  methods: {
    // Toggle sort by supplier
    toggleSortBySupplier() {
      this.sortBySupplier = !this.sortBySupplier
    },

    // Row style class for supplier grouping
    rowStyleClass(row) {
      if (!this.sortBySupplier) {
        return ''
      }
      // Alternate background color for different suppliers
      return row.supplierGroupIndex % 2 === 0 ? 'supplier-group-even' : 'supplier-group-odd'
    },

    // Load repositories for admin
    async loadRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        console.error('Error loading repositories:', error)
      }
    },

    // Load cars
    async loadCars() {
      try {
        const { data } = await axios.get('/api/cars/dropdown/')
        this.cars = data
      } catch (error) {
        this.cars = []
      }
    },

    // Load drivers for the selected repository
    async loadDrivers() {
      this.isLoading = true
      try {
        const params = {}
        if (this.repository?.id) {
          params.repository_id = this.repository.id
        } else if (this.user.repository) {
          params.repository_id = this.user.repository
        }
        const { data } = await axios.get('/api/auth/users/drivers-and-pickupeurs-for-roadmap/', { params })
        this.drivers = data
      } catch (error) {
        this.drivers = []
      }
      this.isLoading = false
    },

    // Load available orders (status = EN_ATTENTE)
    async loadAvailableOrders() {
      this.isLoading = true
      this.selectedOrders = []
      this.selectedDriver = null

      try {
        let repositoryId = null
        if (this.user.role === 'admin' || this.user.role === 'superadmin') {
          repositoryId = this.repository?.id
        } else {
          repositoryId = this.user.repository
        }

        if (!repositoryId) {
          this.availableOrders = []
          this.isLoading = false
          return
        }

        const { data } = await axios.get(`/api/pickups/roadmap/orders/${repositoryId}/`)
        this.availableOrders = data.orders || []
      } catch (error) {
        console.error('Error loading orders:', error)
        this.availableOrders = []
      }
      this.isLoading = false
    },

    // On repository change
    async onRepositoryChange() {
      await this.loadDrivers()
      await this.loadAvailableOrders()
    },

    // Check order by scanning
    async checkOrder() {
      if (!this.ref) return

      // Check if already selected
      const existingSelected = this.selectedOrders.find(o => o.id === this.ref)
      if (existingSelected) {
        this.playAudio(this.alreadyScanned)
        this.showToast('warning', 'top-center', 'Colis déjà sélectionné')
        this.ref = ''
        return
      }

      // Find in available orders
      const order = this.availableOrders.find(o => o.id === this.ref)
      if (order) {
        this.addItem(order)
        this.playAudio(this.ok)
      } else {
        this.playAudio(this.errorVoice)
        this.showToast('danger', 'top-center', 'Colis non trouvé ou non disponible')
      }
      this.ref = ''
    },

    // Add order to selected
    addItem(order) {
      this.selectedOrders.push(order)
      const index = this.availableOrders.findIndex(o => o.id === order.id)
      if (index !== -1) {
        this.availableOrders.splice(index, 1)
      }
    },

    // Remove order from selected
    removeItem(order) {
      this.availableOrders.push(order)
      const index = this.selectedOrders.findIndex(o => o.id === order.id)
      if (index !== -1) {
        this.selectedOrders.splice(index, 1)
      }
    },

    // Create pickup roadmap
    async createPickupRoadmap() {
      // Confirmation dialog
      const summary = `Résumé de la création :\n\n- Livreur : ${this.selectedDriver?.username || ''}\n- Véhicule : ${this.selectedCar?.serial_number || 'Non sélectionné'}\n- Nombre de colis : ${this.selectedOrders.length}`

      const confirmed = await this.$swal({
        title: 'Confirmer la création',
        text: summary,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, créer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })

      if (!confirmed.isConfirmed) return

      this.isLoading = true

      try {
        const payload = {
          orders: this.selectedOrders.map(o => o.id),
          driver_id: this.selectedDriver.id,
        }

        // Add repository for admin
        if (this.user.role === 'admin' || this.user.role === 'superadmin') {
          if (!this.repository?.id) {
            this.showToast('danger', 'top-center', 'Veuillez sélectionner un dépôt')
            this.isLoading = false
            return
          }
          payload.repository_id = this.repository.id
        }

        // Add car if selected
        if (this.selectedCar?.id) {
          payload.car_id = this.selectedCar.id
        }

        const { data } = await axios.post('/api/pickups/roadmap/create/', payload)
        this.showToast('success', 'top-center', 'Feuille de ramassage créée avec succès')
        this.$router.push(`/pickups/roadmap/${data.id}`)
      } catch (error) {
        console.error('Error creating pickup roadmap:', error)
        this.showToast('danger', 'top-center', error.response?.data?.detail || error.toString())
      } finally {
        this.isLoading = false
      }
    },

    // Play audio
    playAudio(audioFile) {
      const audio = new Audio(audioFile)
      audio.play().catch(() => {})
    },

    // Show toast notification
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.save-btn.not-allowed,
.save-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

// Supplier grouping styles
.supplier-group-even {
  background-color: #f8f9fa !important;
}

.supplier-group-odd {
  background-color: #e8f4f8 !important;
}

// Dark mode support
.dark-layout {
  .supplier-group-even {
    background-color: #283046 !important;
  }
  .supplier-group-odd {
    background-color: #1e2a3a !important;
  }
}
</style>
